import PropTypes from 'prop-types';
import { useMemo, useState, useEffect, useReducer, useCallback } from 'react';

import { paths } from 'src/routes/paths';
import { useParams, useRouter } from 'src/routes/hooks';

import { getStorage, useLocalStorage } from 'src/hooks/use-local-storage';

import { useTranslate } from 'src/locales';
import { PATH_AFTER_LOGIN } from 'src/config-global';
import { getEntity, useGetMyEntities } from 'src/api/entity';

import { SplashScreen } from 'src/components/loading-screen';

import { EntityContext } from './entity-context';

const initialState = {
  entity: null,
  loading: true,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      loading: true,
    };
  }
  if (action.type === 'ERROR') {
    return {
      loading: false,
      error: true,
    };
  }
  if (action.type === 'INITIED') {
    return {
      loading: false,
      entity: action.payload.entity,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

async function getLocalSubEntity(mySubEntities = []) {
  const localState = getStorage('entity');
  return mySubEntities.find((se) => se.id === localState?.id);
}

// ----------------------------------------------------------------------

export function EntityProvider({ children }) {
  const { i18n } = useTranslate();
  const router = useRouter();
  const [state, dispatch] = useReducer(reducer, initialState);
  const { entity_id: _entity_id, parent_entity_id } = useParams();
  const { entities: myEntities, entitiesError, entitiesLoading } = useGetMyEntities();
  const [initEntity, setInitEntity] = useState(false);

  const { update } = useLocalStorage('entity');

  // On prends le parent_id si il n'y a pas d'entité dans l'url
  const entity_id = _entity_id || parent_entity_id;

  const initialize = useCallback(async () => {
    try {
      if (!entity_id) {
        return;
      }
      const response = await getEntity(entity_id);
      if (response.error) {
        dispatch({
          type: 'ERROR',
        });
      } else {
        // Tout va bien
        const entity = response.data[0];

        if (entity.type === 'public') {
          i18n.changeLanguage('frPublic');
        } else {
          i18n.changeLanguage('fr');
        }

        let toEntityId = entity.id;
        let parentEntityId = entity.parent;
        if (!parentEntityId) {
          const mySubEntities = entity.entities;
          const localSubEntity = await getLocalSubEntity(mySubEntities);
          if (mySubEntities.length > 0) {
            if (localSubEntity) {
              toEntityId = localSubEntity.id;
              parentEntityId = localSubEntity.parent;
            } else {
              toEntityId = mySubEntities[0].id;
              parentEntityId = entity.id;
            }
          }
        }

        // On met à jour le localStorage uniquement si nécessaire
        const storedEntity = getStorage('entity');
        if (
          !storedEntity ||
          storedEntity.id !== toEntityId ||
          storedEntity.parent !== parentEntityId
        ) {
          update('id', toEntityId);
          update('parent', parentEntityId);
          await new Promise((resolve) => setTimeout(resolve, 0));
        }

        dispatch({
          type: 'INITIED',
          payload: {
            entity,
          },
        });
      }
    } catch (error) {
      if (error.status === 404) {
        window.localStorage.removeItem('entity_id');
        window.location.href = paths.entity.root;
      } else {
        console.error(error);
        dispatch({
          type: 'ERROR',
        });
      }
    }
  }, [entity_id, i18n, update]);

  // Premier chargement de l'application
  useEffect(() => {
    async function getMyEntity() {
      try {
        if (entitiesError) throw new Error(entitiesError);

        // Si on a un entity_id dans l'URL, on vérifie d'abord s'il est valide
        if (entity_id) {
          let targetEntity =
            myEntities.find((me) => `${me.id}` === `${entity_id}`) ||
            myEntities.find((me) => me.entities?.some((se) => `${se.id}` === `${entity_id}`));

          if (!targetEntity) {
            targetEntity = myEntities.find((me) => `${me.parent}` === `${entity_id}`);
          }

          if (targetEntity) {
            if (`${targetEntity.id}` !== `${entity_id}`) {
              update('id', targetEntity.id);
              update('parent', targetEntity.parent);
              router.replace(paths.entity.modules.root(targetEntity.id, targetEntity.parent));
              return;
            }

            // Si c'est une sous-entité
            if (targetEntity.entities?.some((se) => `${se.id}` === `${entity_id}`)) {
              update('id', parseInt(entity_id, 10));
              update('parent', targetEntity.id);
            } else {
              // Si c'est une entité parent
              const firstSubEntity = targetEntity.entities?.[0];
              if (firstSubEntity) {
                update('id', firstSubEntity.id);
                update('parent', targetEntity.id);
              } else {
                update('id', targetEntity.id);
                update('parent', targetEntity.parent);
              }
            }
            await new Promise((resolve) => setTimeout(resolve, 100));
            setInitEntity(true);
            return;
          }
        }

        // Vérifions ensuite si l'entité stockée est valide
        const storedEntity = getStorage('entity');
        if (storedEntity?.id) {
          const isStoredEntityValid = myEntities.some(
            (entity) =>
              entity.id === storedEntity.id ||
              (entity.entities && entity.entities.some((e) => e.id === storedEntity.id))
          );
          if (isStoredEntityValid) {
            router.replace(PATH_AFTER_LOGIN());
            return;
          }
        }

        // Si on arrive ici, on initialise avec la première entité
        const firstEntity = myEntities[0];
        if (!firstEntity) {
          router.replace(paths.error.noDataEntity);
          return;
        }

        let toEntityId = firstEntity.id;
        let parentEntityId = firstEntity.parent;
        if (!parentEntityId) {
          const mySubEntities = firstEntity.entities;
          if (mySubEntities?.length > 0) {
            toEntityId = mySubEntities[0].id;
            parentEntityId = firstEntity.id;
          }
        }

        update('id', toEntityId);
        update('parent', parentEntityId);
        await new Promise((resolve) => setTimeout(resolve, 100));
        router.replace(PATH_AFTER_LOGIN());
      } catch (error) {
        console.error('Error in getMyEntity:', error);
        dispatch({ type: 'ERROR' });
      }
    }

    if (!entitiesLoading && myEntities) {
      getMyEntity();
    }
  }, [entity_id, myEntities, entitiesError, router, entitiesLoading, update]);

  useEffect(() => {
    if (initEntity) {
      initialize();
    }
  }, [initialize, initEntity]);

  // ----------------------------------------------------------------------

  const memoizedValue = useMemo(
    () => ({
      entity: state.entity,
      entityLoading: state.loading,
      myEntities,
      initialize,
      entity_id,
      parent_entity_id,
    }),
    [state.entity, state.loading, myEntities, initialize, entity_id, parent_entity_id]
  );

  if (state.loading || !entity_id) return <SplashScreen />;

  return <EntityContext.Provider value={memoizedValue}>{children}</EntityContext.Provider>;
}

EntityProvider.propTypes = {
  children: PropTypes.node,
};
